import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '../components/PageHeader'
import { TabNavigation } from '../components/TabNavigation'
import { useAuthStore } from '../hooks/useAuthStore'
import { 
  UserPlusIcon,
  UserIcon,
  PhoneIcon,
  AcademicCapIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ArrowRightOnRectangleIcon,
  BanknotesIcon,
  ClockIcon
} from '@heroicons/react/24/outline'

interface Student {
  id: string
  studentCode: string
  name: string
  nickname: string
  level: string
  subjects: string[]
  enrollmentDate: string
  status: 'active' | 'inactive' | 'graduated'
  parent: {
    name: string
    phone: string
    lineId?: string
    relationship: string
  }
  payments: {
    current: 'paid' | 'pending' | 'overdue'
    lastPayment?: string
  }
  attendance: {
    present: number
    total: number
  }
}

export function AdminStudentsPage() {
  const navigate = useNavigate()
  const { logout } = useAuthStore()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'graduated'>('all')
  const [paymentFilter, setPaymentFilter] = useState<'all' | 'paid' | 'pending' | 'overdue'>('all')

  // Mock students data
  const students: Student[] = [
    {
      id: '1',
      studentCode: 'STU-001',
      name: 'ด.ช.ใหม่ สมบูรณ์',
      nickname: 'น้องใหม่',
      level: 'ป.3',
      subjects: ['คณิตศาสตร์', 'ภาษาไทย', 'ภาษาอังกฤษ'],
      enrollmentDate: '2024-07-01',
      status: 'active',
      parent: {
        name: 'คุณสมศรี สมบูรณ์',
        phone: '081-234-5678',
        lineId: 'somjai_line',
        relationship: 'มารดา'
      },
      payments: {
        current: 'pending',
        lastPayment: '2024-07-15'
      },
      attendance: {
        present: 6,
        total: 8
      }
    },
    {
      id: '2',
      studentCode: 'STU-002',
      name: 'ด.ญ.พิณ ดีใจ',
      nickname: 'น้องพิณ',
      level: 'ป.3',
      subjects: ['คณิตศาสตร์', 'ภาษาไทย', 'ภาษาอังกฤษ'],
      enrollmentDate: '2024-06-15',
      status: 'active',
      parent: {
        name: 'คุณมาลี ดีใจ',
        phone: '082-345-6789',
        relationship: 'มารดา'
      },
      payments: {
        current: 'paid',
        lastPayment: '2024-08-01'
      },
      attendance: {
        present: 8,
        total: 8
      }
    },
    {
      id: '3',
      studentCode: 'STU-003',
      name: 'ด.ญ.แนน สวยงาม',
      nickname: 'น้องแนน',
      level: 'ป.3',
      subjects: ['คณิตศาสตร์', 'ภาษาไทย'],
      enrollmentDate: '2024-05-20',
      status: 'active',
      parent: {
        name: 'คุณจิรา สวยงาม',
        phone: '083-456-7890',
        lineId: 'jira_line',
        relationship: 'มารดา'
      },
      payments: {
        current: 'overdue',
        lastPayment: '2024-06-15'
      },
      attendance: {
        present: 5,
        total: 8
      }
    },
    {
      id: '4',
      studentCode: 'STU-004',
      name: 'ด.ญ.มิ้ง น่ารัก',
      nickname: 'น้องมิ้ง',
      level: 'ป.3',
      subjects: ['คณิตศาสตร์', 'ภาษาอังกฤษ'],
      enrollmentDate: '2024-04-10',
      status: 'inactive',
      parent: {
        name: 'คุณสุดา น่ารัก',
        phone: '084-567-8901',
        relationship: 'มารดา'
      },
      payments: {
        current: 'pending'
      },
      attendance: {
        present: 2,
        total: 8
      }
    }
  ]

  const formatThaiDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const thaiYear = date.getFullYear() + 543
    const months = [
      'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
      'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
    ]
    return `${date.getDate()} ${months[date.getMonth()]} ${thaiYear}`
  }

  const getStatusConfig = (status: Student['status']) => {
    const configs = {
      active: { color: 'bg-green-100 text-green-800', label: 'กำลังเรียน', icon: CheckCircleIcon },
      inactive: { color: 'bg-gray-100 text-gray-800', label: 'พักเรียน', icon: ClockIcon },
      graduated: { color: 'bg-blue-100 text-blue-800', label: 'จบแล้ว', icon: AcademicCapIcon }
    }
    return configs[status]
  }

  const getPaymentStatusConfig = (status: Student['payments']['current']) => {
    const configs = {
      paid: { color: 'bg-green-100 text-green-800', label: 'ชำระแล้ว' },
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'รอชำระ' },
      overdue: { color: 'bg-red-100 text-red-800', label: 'เกินกำหนด' }
    }
    return configs[status]
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleAddStudent = () => {
    navigate('/admin/students/add')
  }

  const handleEditStudent = (studentId: string) => {
    navigate(`/admin/students/${studentId}/edit`)
  }

  const handleDeleteStudent = (studentId: string) => {
    // This should navigate to a confirmation page instead of using confirm()
    navigate(`/admin/students/${studentId}/delete`)
  }

  const handleViewStudent = (studentId: string) => {
    navigate(`/admin/students/${studentId}`)
  }

  // Filter students
  const filteredStudents = students
    .filter(student => {
      const matchesSearch = searchTerm === '' || 
        student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.nickname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.studentCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.parent.name.toLowerCase().includes(searchTerm.toLowerCase())
      
      const matchesStatus = statusFilter === 'all' || student.status === statusFilter
      const matchesPayment = paymentFilter === 'all' || student.payments.current === paymentFilter
      
      return matchesSearch && matchesStatus && matchesPayment
    })

  const stats = {
    total: students.length,
    active: students.filter(s => s.status === 'active').length,
    inactive: students.filter(s => s.status === 'inactive').length,
    graduated: students.filter(s => s.status === 'graduated').length,
    pendingPayments: students.filter(s => s.payments.current === 'pending' || s.payments.current === 'overdue').length
  }


  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader 
        title="จัดการนักเรียน"
        showBackButton={true}
        actions={
          <button
            onClick={handleLogout}
            className="text-gray-600 hover:text-red-600 p-2 rounded-lg hover:bg-red-50 transition-colors"
            title="ออกจากระบบ"
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5" />
          </button>
        }
      />

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
            <div className="text-sm text-gray-600">ทั้งหมด</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{stats.active}</div>
            <div className="text-sm text-gray-600">กำลังเรียน</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-gray-600">{stats.inactive}</div>
            <div className="text-sm text-gray-600">พักเรียน</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">{stats.graduated}</div>
            <div className="text-sm text-gray-600">จบแล้ว</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-red-600">{stats.pendingPayments}</div>
            <div className="text-sm text-gray-600">ค้างชำระ</div>
          </div>
        </div>

        {/* Add Student Button */}
        <div className="mb-6">
          <button
            onClick={handleAddStudent}
            className="w-full sm:w-auto bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
          >
            <UserPlusIcon className="h-5 w-5 mr-2" />
            เพิ่มนักเรียนใหม่
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="ค้นหาชื่อนักเรียน, ชื่อเล่น, รหัส หรือชื่อผู้ปกครอง..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Status Filter */}
            <div className="flex space-x-2">
              {(['all', 'active', 'inactive', 'graduated'] as const).map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    statusFilter === status
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {status === 'all' ? 'ทั้งหมด' :
                   status === 'active' ? 'กำลังเรียน' :
                   status === 'inactive' ? 'พักเรียน' : 'จบแล้ว'}
                </button>
              ))}
            </div>

            {/* Payment Filter */}
            <div className="flex space-x-2">
              {(['all', 'paid', 'pending', 'overdue'] as const).map((payment) => (
                <button
                  key={payment}
                  onClick={() => setPaymentFilter(payment)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    paymentFilter === payment
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {payment === 'all' ? 'การชำระ' :
                   payment === 'paid' ? 'ชำระแล้ว' :
                   payment === 'pending' ? 'รอชำระ' : 'เกินกำหนด'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Students List */}
        <div className="space-y-4">
          {filteredStudents.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <UserIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">ไม่พบข้อมูล</h3>
              <p className="text-gray-500 mb-4">ไม่พบนักเรียนตามเงื่อนไขที่กำหนด</p>
              <button
                onClick={handleAddStudent}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors inline-flex items-center"
              >
                <UserPlusIcon className="h-5 w-5 mr-2" />
                เพิ่มนักเรียนใหม่
              </button>
            </div>
          ) : (
            filteredStudents.map((student) => {
              const statusConfig = getStatusConfig(student.status)
              const paymentConfig = getPaymentStatusConfig(student.payments.current)
              const StatusIcon = statusConfig.icon

              return (
                <div key={student.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {student.nickname} ({student.name})
                          </h3>
                          <span className="text-sm text-gray-500">
                            {student.studentCode}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <div className="flex items-center text-gray-600 mb-1">
                              <AcademicCapIcon className="h-4 w-4 mr-1" />
                              <span>ระดับ: {student.level}</span>
                            </div>
                            <div className="flex items-center text-gray-600 mb-1">
                              <CalendarDaysIcon className="h-4 w-4 mr-1" />
                              <span>เริ่มเรียน: {formatThaiDate(student.enrollmentDate)}</span>
                            </div>
                            <div className="text-gray-600">
                              วิชา: {student.subjects.join(', ')}
                            </div>
                          </div>
                          
                          <div>
                            <div className="flex items-center text-gray-600 mb-1">
                              <UserIcon className="h-4 w-4 mr-1" />
                              <span>{student.parent.name} ({student.parent.relationship})</span>
                            </div>
                            <div className="flex items-center text-gray-600 mb-1">
                              <PhoneIcon className="h-4 w-4 mr-1" />
                              <span>{student.parent.phone}</span>
                            </div>
                            <div className="text-gray-600">
                              เข้าเรียน: {student.attendance.present}/{student.attendance.total} ครั้ง
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex flex-col items-end space-y-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusConfig.label}
                        </span>
                        
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${paymentConfig.color}`}>
                          <BanknotesIcon className="h-3 w-3 mr-1" />
                          {paymentConfig.label}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-end space-x-2 pt-3 border-t border-gray-200">
                      <button
                        onClick={() => handleViewStudent(student.id)}
                        className="text-blue-600 hover:text-blue-800 px-3 py-1 rounded-lg hover:bg-blue-50 transition-colors text-sm"
                      >
                        ดูรายละเอียด
                      </button>
                      <button
                        onClick={() => handleEditStudent(student.id)}
                        className="text-green-600 hover:text-green-800 px-3 py-1 rounded-lg hover:bg-green-50 transition-colors flex items-center text-sm"
                      >
                        <PencilSquareIcon className="h-4 w-4 mr-1" />
                        แก้ไข
                      </button>
                      <button
                        onClick={() => handleDeleteStudent(student.id)}
                        className="text-red-600 hover:text-red-800 px-3 py-1 rounded-lg hover:bg-red-50 transition-colors flex items-center text-sm"
                      >
                        <TrashIcon className="h-4 w-4 mr-1" />
                        ลบ
                      </button>
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>

      <TabNavigation activeTab="students" />
    </div>
  )
}